import { estClient } from './estClient';
import { JsonParseError } from './httpClient';

const ACTIVITY_TYPE = {
  NOTE: '1',
  LEAD_CREATE: '2',
  LEAD_CHANGE_STATUS: '3',
  UNIT_CREATE: '4',
  UNIT_CHANGE_STATUS: '5',
  /** @deprecated kept because legacy activity rows may still use it */
  PAYMENT_CREATE: '6',
  DEAL_BOOKED: '7',
  LEAD_INTERESTED: '10',
  UNIT_INTERESTED: '11',
  TASK_SOLVED: '12',
  DEAL_BOOK_EXTEND: '13',
  DEAL_PARAMS_CREATE: '14',
  DEAL_PARAMS_UPDATE: '15',
  DEAL_BOOK_CANCELLED: '16',
  DEAL_CANCELED_MESSAGE: '17',
  LEAD_CONTACTS_CHANGED: '18',
  LEAD_CUSTOM_FIELDS_CHANGED: '19',
  UNIT_REMOVE_STATUS: '20',
  LEAD_REMOVE_STATUS: '21',
  UNIT_RESALE_SET: '22',
  UNIT_RESALE_REMOVED: '23',
  UNIT_RESALE_HAPPENS: '24',
  LEAD_RESALE_HAPPENS: '25',
  LEAD_CHAT_ADDED: '26',
  LEAD_CALL_INCOMING: '27',
  LEAD_CALL_INCOMING_MISSED: '28',
  LEAD_CALL_OUTGOING: '29',
  LEAD_CALL_OUTGOING_FAILED: '30',
  NOTE_EXTERNAL: '31',
  LEAD_APPLICATION: '32',
  NOTE_MANAGER: '33',
  LEAD_MERGED: '34',
  TASK_CHANGED: '35',
  DEAL_CLOSED: '36',
  DEAL_BOOK_EXPIRED: '37',
  DEAL_BOOK_CLOSED: '38',
  UNIT_FAST_BOOK: '42',
  UNIT_FAST_SALE: '43',
  UNIT_FAST_CLEAR: '44',
  DEAL_REASSIGNED: '45',
  OPPORTUNITY_OPENED: '39',
  OPPORTUNITY_WON: '40',
  OPPORTUNITY_LOST: '41',
  OPPORTUNITY_REASSIGNED: '46',
  OPPORTUNITY_QUALIFIED: '47',
  OPPORTUNITY_DISQUALIFIED: '48',
  OPPORTUNITY_PROCESSED: '49',
  OPPORTUNITY_PROGRESS_CHANGED: '50',
  OPPORTUNITY_STATE_CHANGED: '51',
} as const;

export type ActivityTypeCode =
  (typeof ACTIVITY_TYPE)[keyof typeof ACTIVITY_TYPE];

export interface Activity {
  id: string;
  /**
   * @example '2023-05-14 17:37:26'
   */
  created_at: string;
  /**
   * @example '2023-05-14 17:37:26'
   */
  updated_at: string;
  created_by: string;
  lead_id: string;
  opportunity_id: string | null;
  unit_id: string | null;
  deal_id: string | null;
  type: string;
  value: '0';

  /**
   * Message may contain HTML
   */
  message: string;

  /**
   * How much time is left for this activity item to be allowed to be edited
   */
  secondsLeftToEdit: number;

  is_deleted: '0' | '1';

  icon: 'history-note';

  author: {
    name: string;

    /**
     * @example 'demo-ru/user/3/376a71963356b6c218fa7dbe4ab3602b.jpg'
     */
    image: string;
  } | null;
}

export interface NoteProps {
  for_id: string;
  type: 'unit_id' | 'lead_id';
  message: string;
}

export interface NoteResponse {
  id: string;
  type: string;
  value: '0';
  is_deleted: '1' | '0';

  lead_id: string | null;
  unit_id: string | null;
  deal_id: string | null;
  message: string;
  icon: string;

  /**
   * @example "2025-04-20 22:49:19"
   */
  created_at: string;

  /**
   * @example "2025-04-20 22:49:19"
   */
  updated_at: string;
  created_by: string;

  author: {
    name: string;
    image: string;
  };
}

class ActivityApi {
  async getUnitActivity(unitId: string): Promise<Activity[]> {
    return estClient.get<Activity[]>(`/api/activity/unit/${unitId}`);
  }

  async getLeadActivity(leadId: string): Promise<Activity[]> {
    return estClient.get<Activity[]>(`/api/activity/lead/${leadId}`);
  }

  async createNote(note: NoteProps): Promise<NoteResponse> {
    return estClient.post<NoteResponse>('/api/activity/create_note', {
      body: note,
    });
  }

  async updateNote({
    id,
    ...body
  }: {
    id: string;
    message: string;
  }): Promise<NoteResponse> {
    return estClient.post<NoteResponse>(`/api/activity/${id}`, {
      body,
    });
  }

  async createNoteBulk(note: {
    type: 'lead_id' | 'unit_id';
    message: string;
    query: string;
  }) {
    try {
      await estClient.post('/api/activity/bulk_notes', {
        body: note,
      });
    } catch (error) {
      if (error instanceof JsonParseError) {
        if (error.statusCode === 200 && !error.body) {
          // it's a valid response for current action
          return;
        }
      }

      throw error;
    }
  }

  async getCallUrl(id: string): Promise<{ url: string }> {
    return estClient.get<{ url: string }>(`/api/binotel/call_url/${id}`);
  }

  get TYPE() {
    return ACTIVITY_TYPE;
  }
}

export const activityApi = new ActivityApi();
